import axios from "axios";

// Prefer env var (works for browser + APK build, as long as you rebuild web assets)
const RAW_ENV_URL = (process.env.REACT_APP_API_URL || "").trim();

const FALLBACK_URL =
  process.env.NODE_ENV === "production"
    ? "https://api.cbtnova.ng"
    : "http://localhost:4000";

// Final base URL (env wins)
export const API_BASE_URL = (RAW_ENV_URL || FALLBACK_URL).replace(/\/+$/, "");

// ✅ Helpful runtime log (you will see this in browser console and Android Logcat: Capacitor/Console)
console.log("API BASE URL USED:", API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000,
  withCredentials: false,
});

// ---------------------------------------------------
// Safe storage helpers (prevents crashes in WebView
// if storage is blocked or unavailable)
// ---------------------------------------------------
function safeGet(storage, key) {
  try {
    return storage?.getItem?.(key) || null;
  } catch {
    return null;
  }
}

function safeRemove(storage, key) {
  try {
    storage?.removeItem?.(key);
  } catch {
    // ignore
  }
}

function safeSet(storage, key, value) {
  try {
    storage?.setItem?.(key, value);
  } catch {
    // ignore
  }
}

// ===================================================
// REQUEST INTERCEPTOR
// Attach JWT + SESSION token
// ===================================================
api.interceptors.request.use(
  (config) => {
    const token = safeGet(window.localStorage, "waec_token");
    const sessionToken = safeGet(window.localStorage, "waec_session");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (sessionToken) {
      config.headers["x-session-token"] = sessionToken;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ===================================================
// RESPONSE INTERCEPTOR
// UX-FIRST + SECURITY-SAFE REDIRECT STRATEGY
// ===================================================
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const code = error.response?.data?.code;
    const method = error.config?.method?.toUpperCase();
    const currentPath = window.location.pathname || "";

    // ===================================================
    // 🔒 SUBSCRIPTION REQUIRED
    // ===================================================
    if (
      status === 403 &&
      (code === "SUBSCRIPTION_REQUIRED" ||
        code === "CBT_SUBSCRIPTION_REQUIRED" ||
        code === "POST_UTME_SUBSCRIPTION_REQUIRED")
    ) {
      /**
       * IMPORTANT UX RULE:
       * - GET → show premium banner (NO redirect)
       * - POST/PUT/PATCH/DELETE → redirect
       */
      const isActionRequest = method && method !== "GET";

      if (isActionRequest) {
        const safePaths = [
          "/dashboard/subscribe-required",
          "/dashboard/my-subscriptions",
          "/dashboard/pricing",
          "/pricing",
        ];

        const alreadySafe = safePaths.some((p) => currentPath.startsWith(p));

        if (!alreadySafe) {
          safeSet(
            window.sessionStorage,
            "subscribe_reason",
            error.response?.data?.message ||
              "This feature requires an active subscription."
          );

          window.location.href = "/dashboard/subscribe-required";
        }
      }

      return Promise.reject(error);
    }

    // ===================================================
    // 🚪 AUTH FAILURE → HOME
    // ===================================================
    if (status === 401) {
      const backendMessage =
        error.response?.data?.message ||
        "Your session expired or you logged in on another device.";

      safeSet(window.sessionStorage, "logout_reason", backendMessage);

      safeRemove(window.localStorage, "waec_token");
      safeRemove(window.localStorage, "waec_session");
      safeRemove(window.localStorage, "waec_user");

      if (currentPath !== "/") {
        window.location.href = "/login";
      }

      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// ===================================================
// BACKEND WARM-UP (Render cold start protection)
// ===================================================
export async function warmUpBackend() {
  try {
    await api.get("/auth/ping");
  } catch {
    // silent
  }
}

export default api;
